<template>
    <FilterBase
        :narrow
        :title
    >
        <InputRangeTwoKnobbedExponentialOkWithInputs
            v-model="calculatedModel"
            :bounds="{
                min: Number(props.bounds.min.amount),
                max: Number(props.bounds.max.amount),
            }"
            :debounce="500"
            :linear-percent="90"
            :max-linear="5_000"
            :steps="1_000_000"
            :value-formatter
        />
    </FilterBase>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
    NonSkuPriceRangeFragment,
    NonSkuPriceRangeInput,
} from '@/graphql/api'
import type { Bounds } from 'wucdbm-exponential-slider'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import type { InputRangeTwoKnobbedValueFormatter } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import InputRangeTwoKnobbedExponentialOkWithInputs from '@/components/ui/inputs/range/InputRangeTwoKnobbedExponentialOkWithInputs.vue'
import { moneyInputOrMoneyFragmentAmount } from '@/domain'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'
import { useCurrency } from '@/plugins/currency.ts'

const props = withDefaults(
    defineProps<{
        modelValue: NonSkuPriceRangeInput | null | undefined
        bounds: NonSkuPriceRangeFragment
        title?: string
        narrow?: boolean
    }>(),
    {
        modelValue: null,
        title: undefined,
    }
)

const emit = defineEmits<{
    'update:modelValue': [v: NonSkuPriceRangeInput]
}>()

const currencyInput = useCurrencyInput()

const calculatedModel = computed({
    get(): Bounds {
        return {
            min: moneyInputOrMoneyFragmentAmount(
                props.modelValue?.min,
                props.bounds.min
            ),
            max: moneyInputOrMoneyFragmentAmount(
                props.modelValue?.max,
                props.bounds.max
            ),
        }
    },
    set(v: Bounds) {
        const min = v.min
        const max = v.max

        emit('update:modelValue', {
            min: min
                ? {
                      amount: min.toString(),
                      currency: currencyInput.value,
                  }
                : null,
            max: max
                ? {
                      amount: max.toString(),
                      currency: currencyInput.value,
                  }
                : null,
        })
    },
})

const currency = useCurrency()

const moneyFormatter = useMoneyFormatter()

const valueFormatter: InputRangeTwoKnobbedValueFormatter = (value) =>
    moneyFormatter({
        amount: value.toString(),
        currency: currency.value,
    })
</script>
